import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import { Notification } from '@tanium/react-persistent-notifications';
import { Shimmer } from '@tanium/react-shimmer';
import { Stack } from '@tanium/react-stack';
import { AsyncStatus } from '../../hooks/useAsync';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';
import { TaniumSelect } from '../../models/TaniumSelectModels';
import { CloudRoutes, getCloudInstanceRoutes } from '../../Routes';
import { MediumBoldText } from '../../TextStyles';
import { canNavigateToCmp, isLinkedCloudInstance } from '../../utilities/instanceHelper';
import { ErrorText } from '../../pages/Login/ErrorText';

interface InstanceOption {
  label: string;
  value: string;
  isDisabled: boolean;
}

interface ConsoleIdpInstancePickerProps {
  showInstanceFriendlyNames?: boolean;
  onNavigate?: () => void;
}

export const ConsoleIdpInstancePicker: React.VFC<ConsoleIdpInstancePickerProps> = ({
  showInstanceFriendlyNames = false,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { linkedTaniumInstances = [], loadLinkedTaniumInstances } = useTaniumInstances();
  const isLoading = loadLinkedTaniumInstances?.status === AsyncStatus.Pending;
  const hasFailed = loadLinkedTaniumInstances?.status === AsyncStatus.Failure;

  const [pickedId, setPickedId] = useState<string>();
  const [showSelectionError, setShowSelectionError] = useState(false);

  const options = useMemo(
    (): InstanceOption[] =>
      linkedTaniumInstances.filter(isLinkedCloudInstance).map((i) => ({
        label: showInstanceFriendlyNames && i.friendlyName ? `${i.friendlyName} — ${i.url}` : i.url,
        value: i.id,
        isDisabled: !canNavigateToCmp(i),
      })),
    [linkedTaniumInstances, showInstanceFriendlyNames]
  );

  const enabledOptions = options.filter((o) => !o.isDisabled);
  const pickedOption = enabledOptions.find((o) => o.value === pickedId);
  const selectedOption = pickedOption ?? (enabledOptions.length === 1 ? enabledOptions[0] : undefined);
  const selectedId = selectedOption?.value;

  const goToInstance = () => {
    if (!selectedId) {
      setShowSelectionError(true);
      return;
    }
    const administrationRoute = getCloudInstanceRoutes(t)[CloudRoutes.Administration];
    history.push(`${administrationRoute.url}?id=${encodeURIComponent(selectedId)}`);
    onNavigate?.();
  };

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <Box mt={2} height={1} width="100%">
        <Shimmer />
      </Box>
    );
  } else if (hasFailed) {
    content = (
      <Notification role="alert" type="error">
        {t('Failed to load Tanium Instances')}
      </Notification>
    );
  } else if (enabledOptions.length === 0) {
    content = (
      <Notification role="note" type="info">
        {t('No Active Tanium Cloud instances are available.')}
      </Notification>
    );
  } else {
    content = (
      <>
        <Stack direction="row" itemSpacing={2} alignItems="center" flexWrap="wrap">
          <Box width="100%" maxWidth={400}>
            <TaniumSelect
              aria-label={t('Tanium Instance')}
              placeholder={t('Select an instance...')}
              options={options}
              isMulti={false}
              value={selectedOption ?? null}
              invalid={showSelectionError}
              onChange={(option: InstanceOption) => {
                setPickedId(option.value);
                setShowSelectionError(false);
              }}
            />
          </Box>
          <Button variant="primary" size="lg" onClick={goToInstance}>
            {t('Go to instance')}
          </Button>
        </Stack>
        {showSelectionError && <ErrorText role="alert">{t('Select an instance first.')}</ErrorText>}
      </>
    );
  }

  return (
    <Stack direction="column" itemSpacing={2}>
      <MediumBoldText>{t('Tanium Console Identity Provider Settings')}</MediumBoldText>
      {content}
    </Stack>
  );
};
